import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { Typography, Tooltip, Breadcrumbs, IconButton, Box, Alert, Snackbar } from '@mui/material';
import MUIDataTable from 'mui-datatables';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AlertTriangle } from 'lucide-react';
import swal from 'sweetalert';
import Page from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import adminServices from '../../../services/adminServices';
import RoleDrawer from './RoleDrawer';

const readError = (response, fallback) => response?.data?.message || fallback;

export default function Roles() {
  const classes = useStyles();
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [toast, setToast] = useState(null);
  const [endpoints, setEndpoints] = useState(null);

  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchKey, setSearchKey] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('view');
  const [drawerRecord, setDrawerRecord] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerError, setDrawerError] = useState(null);

  const getRoles = useCallback(async (pageNum, rows, searchWord) => {
    setRoles([]);
    setLoading(true);
    setListError(null);
    try {
      const response = await adminServices.getRoles(pageNum, rows, searchWord, true);
      if (response.ok) {
        setRoles(response.data?.records || []);
        setPageCount(response.data?.metadata?.totalRecordCount || 0);
      } else {
        setListError(readError(response, 'Failed to load roles.'));
      }
    } catch (error) {
      console.log(error, 'failed to [getRoles]');
      setListError('Failed to load roles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getRoles(1, rowsPerPage, searchKey);
    // The endpoint catalogue backs the permission picker and never changes at runtime,
    // so it is fetched once for the page rather than per drawer open.
    adminServices.getRoleEndpoints().then((res) => {
      if (res.ok) setEndpoints(res.data?.endpoints || null);
    }).catch((error) => console.log(error, 'failed to [getRoleEndpoints]'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => getRoles(page + 1, rowsPerPage, searchKey);

  const openDrawerFor = async (id, mode) => {
    setDrawerMode(mode);
    setDrawerRecord(null);
    setDrawerError(null);
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const response = await adminServices.getRoleById(id);
      if (response.ok) setDrawerRecord(response.data?.record || null);
      else setDrawerError(readError(response, 'Failed to load this role.'));
    } catch (error) {
      console.log(error, 'failed to [getRoleById]');
      setDrawerError('Failed to load this role.');
    } finally {
      setDrawerLoading(false);
    }
  };

  const openCreateDrawer = () => {
    setDrawerMode('create');
    setDrawerRecord(null);
    setDrawerError(null);
    setDrawerLoading(false);
    setDrawerOpen(true);
  };

  const handleSubmit = async (payload) => {
    setDrawerSaving(true);
    setDrawerError(null);
    try {
      const response = drawerMode === 'create'
        ? await adminServices.createRole(payload)
        : await adminServices.updateRole(drawerRecord.id, payload);

      if (response.ok) {
        setDrawerOpen(false);
        setToast(response.data?.message || 'Saved successfully.');
        reload();
      } else {
        setDrawerError(readError(response, 'Failed to save this role.'));
      }
    } catch (error) {
      console.log(error, 'failed to [saveRole]');
      setDrawerError('Failed to save this role.');
    } finally {
      setDrawerSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const confirmed = await swal({
      title: 'Delete this role?',
      text: `"${row.code}" will be removed permanently.`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });
    if (!confirmed) return;

    try {
      const response = await adminServices.deleteRole(row.id);
      if (response.ok) {
        setToast(response.data?.message || 'Role deleted.');
        reload();
      } else {
        swal({ title: 'Could not delete', text: readError(response, 'Failed to delete this role.'), icon: 'error' });
      }
    } catch (error) {
      console.log(error, 'failed to [deleteRole]');
      swal({ title: 'Could not delete', text: 'Failed to delete this role.', icon: 'error' });
    }
  };

  const columns = [
    { name: 'id', label: 'Id', options: { filter: false, sort: false, display: false } },
    {
      name: 'code',
      label: 'Role Code',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const row = roles[i];
          if (!row) return null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography className={classes.mono}>{row.code}</Typography>
              {row.isSystem && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-gray-50 text-gray-500 border-gray-300">
                  Built-in
                </span>
              )}
            </Box>
          );
        },
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: { filter: false, sort: false, customBodyRender: (v) => <Typography className={classes.name}>{v || '-'}</Typography> },
    },
    {
      name: 'userCount',
      label: 'Users',
      options: { filter: false, sort: false, customBodyRender: (v) => <Typography className={classes.name}>{v}</Typography> },
    },
    {
      name: 'layout',
      label: 'Layout',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const row = roles[i];
          if (!row) return null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                row.layout ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-gray-50 text-gray-400 border-gray-200'
              }`}>
                {row.layout || 'default'}
              </span>
              <Tooltip title={row.allowedPageCount ? 'Restricted to selected pages' : 'All pages in this layout'}>
                <Typography className={classes.name} sx={{ fontSize: '11.5px', color: '#94a3b8' }}>
                  {row.allowedPageCount ? `${row.allowedPageCount} pages` : 'all pages'}
                </Typography>
              </Tooltip>
            </Box>
          );
        },
      },
    },
    {
      name: 'permissionCount',
      label: 'Permissions',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const row = roles[i];
          if (!row) return null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography className={classes.name}>{row.permissionCount}</Typography>
              {row.usesJsonFallback && (
                <Tooltip title="No stored permissions yet — this role is using the built-in defaults.">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full border bg-blue-50 text-blue-600 border-blue-300">
                    Default
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    },
    {
      name: 'isActive',
      label: 'Status',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
            v ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-50 text-gray-500 border-gray-300'
          }`}>
            {v ? 'Active' : 'Inactive'}
          </span>
        ),
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (i) => {
          const row = roles[i];
          if (!row) return null;
          return (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="View">
                <IconButton size="small" onClick={() => openDrawerFor(row.id, 'view')}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openDrawerFor(row.id, 'edit')}>
                  <ModeEditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={row.isSystem ? 'Built-in roles cannot be deleted' : 'Delete'}>
                <span>
                  <IconButton size="small" color="error" disabled={row.isSystem} onClick={() => handleDelete(row)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    },
  ];

  const Breadcrumb = () => (
    <Breadcrumbs aria-label="breadcrumb" separator="›">
      <Typography sx={{ textDecoration: 'none' }} variant="body2" color="primary" component={RouterLink} to="/app/dashboard">
        Dashboard
      </Typography>
      <Typography variant="body2" color="textPrimary">
        Roles &amp; Permissions
      </Typography>
    </Breadcrumbs>
  );

  return (
    <Page title="Roles & Permissions">
      <div className="px-4 md:px-6 pt-4 pb-12 min-h-screen bg-gray-50">
        <PageHeader
          pageTitle="Roles & Permissions"
          subTitle="Roles and the API endpoints each one is allowed to call."
          breadcrumbs={<Breadcrumb />}
          buttonText="Add Role"
          handleButtonClick={openCreateDrawer}
          onBack={() => navigate('/app/dashboard')}
        />

        {listError && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
            <p className="text-sm text-red-600">{listError}</p>
            <button type="button" onClick={reload} className="ml-auto text-xs font-semibold text-red-700 bg-transparent border-0 cursor-pointer">
              Retry
            </button>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden [&_.MuiPaper-root]:shadow-none [&_.MuiPaper-root]:rounded-none [&_.MuiPaper-root]:bg-transparent">
          <MUIDataTable
            data={roles}
            columns={columns}
            options={{
              pagination: true,
              filter: false,
              responsive: 'scroll',
              print: false,
              jumpToPage: true,
              download: false,
              page,
              search: true,
              count: pageCount,
              serverSide: true,
              rowsPerPage,
              rowsPerPageOptions: [10, 25, 50, 75, 100],
              selectableRows: false,
              textLabels: {
                body: { noMatch: loading ? 'loading' : 'sorry, no matching records found' },
                pagination: { jumpToPage: 'jumpToPage', rowsPerPage: 'rowsPerPage' },
              },
              onTableChange: (action, tableState) => {
                if (!['changeRowsPerPage', 'changePage', 'search'].includes(action)) return;
                const nextSearchKey = tableState.searchText !== null ? tableState.searchText : '';
                setRowsPerPage(tableState.rowsPerPage);
                setRoles([]);
                setPage(tableState.page);
                setSearchKey(nextSearchKey);
                getRoles(tableState.page + 1, tableState.rowsPerPage, nextSearchKey);
              },
            }}
          />
        </div>
      </div>

      <RoleDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        endpoints={endpoints}
        loading={drawerLoading}
        saving={drawerSaving}
        error={drawerError}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Page>
  );
}

const useStyles = makeStyles((theme) => ({
  name: {
    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '260px',
    [theme.breakpoints.down('md')]: { maxWidth: 'unset' },
  },
  mono: { fontFamily: 'monospace', fontSize: '13px', fontWeight: 600 },
}));
